// Drag-drop + click-to-pick uploader for post attachments.
//
// One file per post for POC (multi-file gallery lands with PH0-BE-12 phase 2).
// Streams the file straight to POST /api/uploads which handles resize +
// content-hashing + storage. On success we get back a permanent URL to bake
// into the post's media_url on submit.

import { useCallback, useRef, useState, type DragEvent } from 'react';
import { useAuth } from '../lib/auth';

interface UploadResult {
    url:            string;
    thumbnail_url?: string;
    width?:         number;
    height?:        number;
    size_bytes:     number;
    content_type:   string;
}

interface Props {
    /** Current attachment; null when nothing uploaded yet. */
    value: UploadResult | null;
    onChange: (v: UploadResult | null) => void;
}

export function MediaUpload({ value, onChange }: Props) {
    const { ensureFresh, token } = useAuth();
    const inputRef = useRef<HTMLInputElement>(null);
    const [dragOver, setDragOver] = useState(false);
    const [error,    setError]    = useState<string>('');
    const [progress, setProgress] = useState<number | null>(null);

    const upload = useCallback(async (file: File) => {
        setError('');
        setProgress(0);
        await ensureFresh();
        const t = token();
        const fd = new FormData();
        fd.append('file', file);

        // Use XHR so we get real progress events (fetch's ReadableStream
        // upload progress isn't shipped in Safari yet).
        const xhr = new XMLHttpRequest();
        xhr.open('POST', '/api/uploads');
        if (t) xhr.setRequestHeader('Authorization', 'Bearer ' + t);
        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
            setProgress(null);
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    onChange(JSON.parse(xhr.responseText) as UploadResult);
                } catch (e) {
                    setError('Bad server response');
                }
            } else {
                try {
                    setError(JSON.parse(xhr.responseText).error || `HTTP ${xhr.status}`);
                } catch { setError(`HTTP ${xhr.status}`); }
            }
        };
        xhr.onerror = () => { setProgress(null); setError('Upload failed'); };
        xhr.send(fd);
    }, [ensureFresh, token, onChange]);

    function onDrop(e: DragEvent<HTMLDivElement>) {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) upload(file);
    }
    function onDragOver(e: DragEvent<HTMLDivElement>) {
        e.preventDefault();
        setDragOver(true);
    }
    function onPick(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        if (file) upload(file);
    }

    if (value) {
        return (
            <div className="media-upload-current">
                <div className="media-preview-wrap">
                    {value.content_type.startsWith('image/') ? (
                        <img src={value.thumbnail_url || value.url} alt="" />
                    ) : value.content_type.startsWith('video/') ? (
                        <video src={value.url} controls muted />
                    ) : (
                        <div className="media-preview-fallback">{value.content_type}</div>
                    )}
                </div>
                <div className="media-preview-meta">
                    <span>
                        {value.width && value.height ? `${value.width}×${value.height}` : ''}
                        {' · '}{Math.round(value.size_bytes / 1024)} KB
                    </span>
                    <button type="button" className="link danger" onClick={() => onChange(null)}>Remove</button>
                </div>
            </div>
        );
    }

    return (
        <div
            className={'media-drop-zone' + (dragOver ? ' is-hover' : '')}
            onDragEnter={onDragOver}
            onDragOver={onDragOver}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            role="button"
            tabIndex={0}
        >
            <input
                ref={inputRef}
                type="file"
                accept="image/*,video/*"
                style={{ display: 'none' }}
                onChange={onPick}
            />
            {progress != null ? (
                <>
                    <div className="drop-zone-primary">Uploading… {progress}%</div>
                    <div className="drop-zone-bar"><div className="drop-zone-fill" style={{ width: progress + '%' }} /></div>
                </>
            ) : (
                <>
                    <div className="drop-zone-primary">Drag an image or video here</div>
                    <div className="drop-zone-secondary">or tap to pick — resized to 1600px, max 20MB</div>
                </>
            )}
            {error && <div className="drop-zone-error">{error}</div>}
        </div>
    );
}

export type { UploadResult };
